/**
 * The pure message lookup: key → ICU MessageFormat → string. No React, no globals.
 *
 * FALLBACK (REQ-10), in order:
 *   1. the active locale's message;
 *   2. the English source message (and a `missing` event is reported);
 *   3. nothing — `""` in production, `⟦key⟧` in development and tests so it cannot be missed.
 *      A raw key is never shown to a player. (Step 3 is unreachable for code that type-checks:
 *      `MessageKey` comes from the English catalog.)
 *
 * A translation that fails to FORMAT (bad ICU from a translator, a missing placeholder value)
 * falls back to English the same way and reports `format_error`.
 */
import { IntlMessageFormat, type PrimitiveType } from "intl-messageformat";
import { DEFAULT_LOCALE, PSEUDO_LOCALE, type AppLocale } from "@shared/i18n/localeConfig";
import type { Catalog } from "./englishCatalog";

export type MessageValues = Record<string, PrimitiveType>;
/** Values for rich messages: primitives, plus a function per `<tag>` that wraps its chunks. */
export type RichMessageValues<T> = Record<string, PrimitiveType | T | ((chunks: Array<string | T>) => T)>;

export type FallbackReason = "missing" | "format_error" | "missing_everywhere";

export interface FallbackEvent {
  readonly key: string;
  readonly locale: AppLocale;
  readonly reason: FallbackReason;
}

export interface TranslatorOptions {
  readonly locale: AppLocale;
  readonly messages: Catalog;
  readonly fallback: Catalog;
  readonly onFallback?: (event: FallbackEvent) => void;
  /** Show `⟦key⟧` for a key no catalog has. Off in production. */
  readonly revealMissingKeys?: boolean;
}

/** The locale ICU should use for plural rules and number formatting. */
function formattingLocale(locale: AppLocale): string {
  return locale === PSEUDO_LOCALE ? DEFAULT_LOCALE : locale;
}

export class Translator {
  readonly locale: AppLocale;
  private readonly cache = new Map<string, IntlMessageFormat>();

  constructor(private readonly options: TranslatorOptions) {
    this.locale = options.locale;
  }

  /** True when the ACTIVE locale has its own message for `key` (no fallback needed). */
  has(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.options.messages, key);
  }

  t(key: string, values?: MessageValues): string {
    const out = this.format(key, values);
    return Array.isArray(out) ? out.join("") : String(out);
  }

  /** For messages with `<tag>` markup; returns an array of strings and the caller's nodes. */
  rich<T>(key: string, values: RichMessageValues<T>): string | T | Array<string | T> {
    return this.format(key, values) as string | T | Array<string | T>;
  }

  private format(key: string, values?: Record<string, unknown>): unknown {
    const { messages, fallback, onFallback, revealMissingKeys } = this.options;
    const own = this.has(key) ? messages[key] : undefined;

    if (own !== undefined) {
      const formatted = this.tryFormat(`${this.locale}\u0000${key}`, own, formattingLocale(this.locale), values);
      if (formatted.ok) return formatted.value;
      onFallback?.({ key, locale: this.locale, reason: "format_error" });
    } else if (this.locale !== DEFAULT_LOCALE) {
      onFallback?.({ key, locale: this.locale, reason: "missing" });
    }

    const source = Object.prototype.hasOwnProperty.call(fallback, key) ? fallback[key] : undefined;
    if (source !== undefined) {
      const formatted = this.tryFormat(`${DEFAULT_LOCALE}\u0000${key}`, source, DEFAULT_LOCALE, values);
      if (formatted.ok) return formatted.value;
      onFallback?.({ key, locale: DEFAULT_LOCALE, reason: "format_error" });
      return revealMissingKeys ? `⟦${key}⟧` : source;
    }

    onFallback?.({ key, locale: this.locale, reason: "missing_everywhere" });
    return revealMissingKeys ? `⟦${key}⟧` : "";
  }

  private tryFormat(
    cacheKey: string,
    message: string,
    locale: string,
    values: Record<string, unknown> | undefined,
  ): { ok: true; value: unknown } | { ok: false } {
    try {
      let fmt = this.cache.get(cacheKey);
      if (!fmt) {
        fmt = new IntlMessageFormat(message, locale);
        this.cache.set(cacheKey, fmt);
      }
      return { ok: true, value: fmt.format(values as never) };
    } catch {
      return { ok: false };
    }
  }
}
